import { useMemo } from 'react';
import {
    PortfolioEnterpriseRelationshipFilters,
    PortfolioEnterpriseRelationshipResult,
    PortfolioStringFilter,
} from '@mews-commander/data-access';
import { TableState } from '@optimus-web/core';
import { useEnterpriseRelationships } from '../queries/ProductForm';
import { EnterpriseState, useEnterprisesProductContext } from '../context/EnterprisesProductContext';

export type CurrencyCountryTableState = TableState<
    PortfolioEnterpriseRelationshipResult & { defaultCurrencyCode: string },
    PortfolioEnterpriseRelationshipFilters & { defaultCurrencyCode: Array<PortfolioStringFilter> }
>;

export const useProperties = (tableState: CurrencyCountryTableState, selectedEnterpriseIds?: string[]) => {
    const {
        state: { enterprises, selectedEnterpriseIds: ids },
    } = useEnterprisesProductContext();

    const { data, isFetching } = useEnterpriseRelationships(selectedEnterpriseIds || ids);

    const enterprisesById = useMemo(
        () =>
            enterprises.reduce(
                (acc, enterprise) => {
                    acc[enterprise.id] = enterprise;
                    return acc;
                },
                {} as Record<string, EnterpriseState>
            ),
        [enterprises]
    );

    const dataWithDefaultCurrency = useMemo(() => {
        if (!data) {
            return [];
        }

        return data.data.map((row) => {
            const enterprise = enterprisesById[row.id];
            return {
                ...row,
                defaultCurrencyCode: enterprise?.defaultCurrencyCode,
            };
        });
    }, [data, enterprisesById]);

    const { selectedCountries, selectedCurrencies }: { selectedCountries: string[]; selectedCurrencies: string[] } =
        useMemo(() => {
            const countries = new Set<string>();
            const currencies = new Set<string>();

            dataWithDefaultCurrency.forEach((item) => {
                countries.add(item.countryName);
                currencies.add(item.defaultCurrencyCode);
            });

            return {
                selectedCountries: Array.from(countries),
                selectedCurrencies: Array.from(currencies),
            };
        }, [dataWithDefaultCurrency]);

    const filteredData = useMemo(() => {
        const countryFilter = tableState.filters?.countryName;
        const currencyFilter = tableState.filters?.defaultCurrencyCode;

        if (!countryFilter?.length && !currencyFilter?.length) {
            return dataWithDefaultCurrency;
        }

        return dataWithDefaultCurrency.filter((row) => {
            const matchesCountry = !countryFilter?.length || countryFilter[0].value.includes(row.countryName);
            const matchesCurrency =
                !currencyFilter?.length || currencyFilter[0].value.includes(row.defaultCurrencyCode);

            return matchesCountry && matchesCurrency;
        });
    }, [dataWithDefaultCurrency, tableState.filters?.countryName, tableState.filters?.defaultCurrencyCode]);

    return {
        enterprisesById,
        filteredData,
        dataWithDefaultCurrency,
        selectedCurrencies,
        selectedCountries,
        isFetching,
    };
};
